/**
 * Fase 2 (W1, A5 5.10): regras do seletor de período do Início, separadas do componente para teste.
 * Datas sempre AAAA-MM-DD no fuso do workspace (nunca `toISOString()`, que é UTC: entre 21h e meia-noite no Brasil o
 * "hoje" já seria amanhã, E2 do antigo).
 */
export const ATALHOS_PERIODO = ['hoje', 'semana', 'mes', 'mes_passado', '30_dias', 'trimestre', 'ano'] as const;
export type AtalhoPeriodo = (typeof ATALHOS_PERIODO)[number];
export type EscolhaPeriodo = AtalhoPeriodo | 'personalizado';

export interface Periodo {
  de: string;
  ate: string;
}

/** Período máximo aceito (o mesmo teto da API). */
export const MAX_DIAS_PERIODO = 3 * 366;

const DIA_MS = 86_400_000;
const paraDia = (iso: string) => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
const deDia = (ms: number) => {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};
const ultimoDia = (ano: number, mes: number) => deDia(Date.UTC(ano, mes, 0));

/** Hoje (AAAA-MM-DD) no fuso informado; fuso inválido cai no fuso do aparelho. */
export function hojeNoFuso(fuso: string, agora: Date = new Date()): string {
  try {
    const partes = new Intl.DateTimeFormat('en-CA', { timeZone: fuso, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(agora);
    const v = (t: string) => partes.find((p) => p.type === t)?.value ?? '';
    return `${v('year')}-${v('month')}-${v('day')}`;
  } catch {
    return `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}-${String(agora.getDate()).padStart(2, '0')}`;
  }
}

/** Período de cada atalho a partir de hoje. Semana de segunda a domingo; trimestre e ano do calendário. */
export function periodoDoAtalho(atalho: AtalhoPeriodo, hoje: string): Periodo {
  const ano = Number(hoje.slice(0, 4));
  const mes = Number(hoje.slice(5, 7));
  switch (atalho) {
    case 'hoje':
      return { de: hoje, ate: hoje };
    case 'semana': {
      const dia = new Date(paraDia(hoje)).getUTCDay();
      const segunda = paraDia(hoje) - ((dia + 6) % 7) * DIA_MS;
      return { de: deDia(segunda), ate: deDia(segunda + 6 * DIA_MS) };
    }
    case 'mes':
      return { de: `${hoje.slice(0, 7)}-01`, ate: ultimoDia(ano, mes) };
    case 'mes_passado': {
      const [a, m] = mes === 1 ? [ano - 1, 12] : [ano, mes - 1];
      return { de: `${a}-${String(m).padStart(2, '0')}-01`, ate: ultimoDia(a, m) };
    }
    case '30_dias':
      return { de: deDia(paraDia(hoje) - 29 * DIA_MS), ate: hoje };
    case 'trimestre': {
      const inicio = Math.floor((mes - 1) / 3) * 3 + 1;
      return { de: `${ano}-${String(inicio).padStart(2, '0')}-01`, ate: ultimoDia(ano, inicio + 2) };
    }
    case 'ano':
      return { de: `${ano}-01-01`, ate: `${ano}-12-31` };
  }
}

/**
 * Fase 2 (W33, item 1): abas do painel do seletor, na estrutura do antigo (Atalhos, Mês específico e Período). O painel abre
 * na aba que corresponde ao período em vigor: atalho em Atalhos, mês inteiro do calendário em Mês específico, o resto em Período.
 */
export const ABAS_PERIODO = ['atalhos', 'mes', 'periodo'] as const;
export type AbaPeriodo = (typeof ABAS_PERIODO)[number];

const RE_MES = /^(\d{4})-(0[1-9]|1[0-2])$/;

/** Período de um mês do calendário (`AAAA-MM`, ano de 1900 a 2100, como o campo de data); null quando o texto não é um mês. */
export function periodoDoMes(chave: string | null | undefined): Periodo | null {
  const m = RE_MES.exec(chave ?? '');
  if (!m) return null;
  const ano = Number(m[1]);
  if (ano < 1900 || ano > 2100) return null;
  return { de: `${m[1]}-${m[2]}-01`, ate: ultimoDia(ano, Number(m[2])) };
}

/** `AAAA-MM` quando o período é exatamente um mês inteiro do calendário; null nos outros. */
export function mesDoPeriodo(p: Periodo): string | null {
  const x = periodoDoMes(p.de.slice(0, 7));
  return x && x.de === p.de && x.ate === p.ate ? p.de.slice(0, 7) : null;
}

/** Aba em que o painel abre para o período em vigor. */
export function abaDoPeriodo(escolha: EscolhaPeriodo, p: Periodo): AbaPeriodo {
  if (escolha !== 'personalizado') return 'atalhos';
  return mesDoPeriodo(p) ? 'mes' : 'periodo';
}

/** Escolha do botão "Aplicar" da aba Mês específico (guardada com as datas: o mês escolhido não anda com o calendário). */
export function aplicarMes(chave: string): PeriodoGuardado | null {
  const p = periodoDoMes(chave);
  return p ? { atalho: 'personalizado', ...p } : null;
}

/** Escolha do botão "Aplicar período" da aba Período: datas válidas, na ordem e com no máximo 3 anos. */
export function aplicarIntervalo(de: string, ate: string): PeriodoGuardado | null {
  return periodoValido({ de, ate }) ? { atalho: 'personalizado', de, ate } : null;
}

/** Datas de uma escolha: as do atalho (relativas a hoje) ou as guardadas. */
export function periodoDaEscolha(e: PeriodoGuardado, hoje: string): Periodo {
  return e.atalho === 'personalizado' ? { de: e.de, ate: e.ate } : periodoDoAtalho(e.atalho, hoje);
}

/** A busca da URL com o período (`?de=&ate=`, link compartilhável), sem mexer nos outros parâmetros da tela. */
export function buscaComPeriodo(atual: URLSearchParams, p: Periodo): URLSearchParams {
  const n = new URLSearchParams(atual);
  n.set('de', p.de);
  n.set('ate', p.ate);
  return n;
}

/** Qual atalho gera exatamente esse período hoje (ou "personalizado"). */
export function escolhaDoPeriodo(p: Periodo, hoje: string): EscolhaPeriodo {
  return ATALHOS_PERIODO.find((a) => {
    const x = periodoDoAtalho(a, hoje);
    return x.de === p.de && x.ate === p.ate;
  }) ?? 'personalizado';
}

const RE_DATA = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/** Período válido: datas existentes, "de" até "ate" e no máximo 3 anos. */
export function periodoValido(p: Partial<Periodo> | null | undefined): p is Periodo {
  if (!p?.de || !p.ate || !RE_DATA.test(p.de) || !RE_DATA.test(p.ate)) return false;
  if (deDia(paraDia(p.de)) !== p.de || deDia(paraDia(p.ate)) !== p.ate) return false;
  if (p.ate < p.de) return false;
  return (paraDia(p.ate) - paraDia(p.de)) / DIA_MS + 1 <= MAX_DIAS_PERIODO;
}

/** O que fica guardado no aparelho: o atalho (relativo: "mês atual" no mês que vem é o mês que vem) ou as datas. */
export type PeriodoGuardado = { atalho: AtalhoPeriodo } | { atalho: 'personalizado'; de: string; ate: string };

export const chavePeriodo = (usuarioId: string) => `malgoos.inicio.periodo.${usuarioId}`;

export function lerPeriodoGuardado(texto: string | null): PeriodoGuardado | null {
  if (!texto) return null;
  try {
    const v = JSON.parse(texto) as Partial<{ atalho: string; de: string; ate: string }>;
    if ((ATALHOS_PERIODO as readonly string[]).includes(v.atalho ?? '')) return { atalho: v.atalho as AtalhoPeriodo };
    if (v.atalho === 'personalizado' && periodoValido({ de: v.de, ate: v.ate })) return { atalho: 'personalizado', de: v.de!, ate: v.ate! };
  } catch {
    /* valor estranho: ignora */
  }
  return null;
}

/**
 * Período vigente: o da URL (`?de=&ate=`, link compartilhável) vence; senão o guardado no aparelho; senão o mês atual.
 */
export function resolverPeriodo(url: { de: string | null; ate: string | null }, guardado: PeriodoGuardado | null, hoje: string): Periodo {
  const daUrl = { de: url.de ?? undefined, ate: url.ate ?? undefined };
  if (periodoValido(daUrl)) return daUrl;
  if (guardado?.atalho === 'personalizado') return { de: guardado.de, ate: guardado.ate };
  if (guardado) return periodoDoAtalho(guardado.atalho, hoje);
  return periodoDoAtalho('mes', hoje);
}
